import {
  useMemo, useEffect, useCallback, useRef, useState
} from 'react'
import {
  entities as entitiesAction,
  orderbook as orderbookAction,
  // orderbookCleanup as orderbookCleanupAction,
  aggTrades as aggTradesAction
} from 'actions'
import {useDispatch} from 'redux-react-hook'
import {WS_ROOT} from 'constants'
import {SpeedApiSdk} from 'services/callApi'
import {futuresStreamers} from '@binance/js-sdk'
import {schedule} from 'utils/scheduler'
import PERFORMANCE from 'performance'

const {TRADES_FETCH_LIMIT, DEPTH_FETCH_LIMIT, TRADES_UPDATE_MINIMUM_INTERVAL} = PERFORMANCE
const EVENT_LOOP_TOLERANCE = 20
const MAX_WAIT = 1000

function useTradeStreamer (symbol) {
  const dispatch = useDispatch()
  const lastUpdateRef = useRef(null)
  const lastLoaded = useRef(false)

  const fetchData = useCallback(() => {
    lastLoaded.current = true
    SpeedApiSdk.futures.get24hourTicker(symbol).then(({ticker, error}) => {
      if (!error) dispatch(entitiesAction({tickers: {[symbol]: ticker}}))
    })

    SpeedApiSdk.futures
    .getAggTrades({symbol, limit: TRADES_FETCH_LIMIT})
    .then(({aggTrades = {}, error}) => {
      if (!error) dispatch(aggTradesAction(symbol, Object.values(aggTrades)))
    })
  }, [dispatch, symbol])

  const fetchOrderbook = useCallback(() => {
    SpeedApiSdk.futures
    .getOrderBook({symbol, limit: DEPTH_FETCH_LIMIT})
    .then(({error, ...orderbook}) => {
      if (!error) dispatch(orderbookAction(symbol, {...orderbook}, true))
    })
  }, [dispatch, symbol])

  const aggTradeDispatch = useCallback(
    ({symbol: s, response}) => dispatch(aggTradesAction(s, response)),
    [dispatch]
  )

  const orderbookDispatch = useCallback(
    ({symbol: s, response}) => dispatch(orderbookAction(s, response)),
    [dispatch]
  )

  const socketReceivedTrade = schedule(aggTradeDispatch, {
    wait: TRADES_UPDATE_MINIMUM_INTERVAL,
    tolerance: EVENT_LOOP_TOLERANCE,
    maxWait: MAX_WAIT,
    init: () => ({
      symbol,
      response: []
    }),
    collect ({response}, {response: aggTrades}) {
      aggTrades.unshift(response)
    },
    test: ({response}) => response.length > 0
  })

  const socketReceivedOrderbook = schedule(orderbookDispatch, {
    wait: TRADES_UPDATE_MINIMUM_INTERVAL,
    tolerance: EVENT_LOOP_TOLERANCE,
    maxWait: MAX_WAIT,
    init: () => ({
      symbol,
      response: {asks: {}, bids: {}}
    }),
    collect ({response}, {response: orderbook}) {
      orderbook.asks = {...orderbook?.asks, ...response?.asks}
      orderbook.bids = {...orderbook?.bids, ...response?.bids}
    },
    test: ({response: {asks, bids}}) => Object.keys(asks).length || Object.keys(bids).length
  })

  const tradeStreamer = useMemo(() => new futuresStreamers.TradeStreamer(WS_ROOT, symbol), [symbol])
  const onReceive = useCallback(
    eventData => {
      const {stream, data} = eventData
      const sym = symbol.toLowerCase()
      const tickerKey = `${sym}@miniTicker`
      const depthKey = `${sym}@depth`
      const aggTradeKey = `${sym}@aggTrade`
      const markPriceKey = `${sym}@markPrice`
      if (stream === tickerKey) {
        dispatch(entitiesAction(data.entities))
      } else if (stream === depthKey) {
        const {u, pu} = data.result.id
        if (pu !== lastUpdateRef.current) {
          console.log('数据断层', lastUpdateRef.current, pu)
          fetchOrderbook()
        }
        lastUpdateRef.current = u
        socketReceivedOrderbook({symbol, response: data.entities})
      } else if (stream === aggTradeKey) {
        const response = Object.values(data.entities.aggTrades)[0]
        socketReceivedTrade({symbol, response})
      } else if (stream === markPriceKey) {
        dispatch(entitiesAction({tickers: data.entities.markPrice}))
        dispatch(entitiesAction({positions: data.entities.markPrice}))
      }
    },
    [dispatch, fetchOrderbook, socketReceivedOrderbook, socketReceivedTrade, symbol]
  )

  useEffect(() => {
    if (!symbol || lastLoaded.current) return
    fetchData()
    fetchOrderbook()
    if (tradeStreamer.getState() === 'CONNECTING') {
      tradeStreamer.unsubscribe()
    }
    tradeStreamer.subscribe(onReceive)
  }, [fetchData, fetchOrderbook, onReceive, symbol, tradeStreamer])
}

export default useTradeStreamer
